/**
 * Utilidades sobre el arbol de menus que devuelve `GET /api/v1/menus/`.
 *
 * El arbol ya viene podado por permisos: si un nodo esta aca, el usuario puede
 * entrar. El frontend no vuelve a decidir sobre seguridad, solo sobre navegacion.
 */

import type { Domain, MenuNode } from '@/lib/types';

/** Nombre del parametro de busqueda que identifica el menu de origen. */
export const MENU_PARAM = 'menu';

/**
 * Nombre del parametro que transporta un dominio base arbitrario en la URL.
 *
 * Un menu lleva su filtro por NOMBRE (`?menu=`) y el nucleo lo resuelve contra el
 * registry; un boton inteligente no tiene menu detras, asi que lleva su dominio
 * ya resuelto directamente en la URL. En los dos casos el filtro se aplica SIEMPRE
 * y el usuario no lo puede quitar, y el backend lo revalida como cualquier dominio
 * de cliente: ponerlo en la URL no lo hace confiable, solo navegable y compartible.
 */
export const DOMAIN_PARAM = 'domain';

/**
 * Ruta de un modelo con un dominio base aplicado, para la navegacion de un boton
 * inteligente. El dominio viaja JSON-encodeado en `?domain=`.
 */
export function filteredModelPath(modelName: string, domain: Domain): string {
  const base = `/m/${encodeURIComponent(modelName)}`;
  if (domain.length === 0) return base;
  return `${base}?${DOMAIN_PARAM}=${encodeURIComponent(JSON.stringify(domain))}`;
}

/** Interpreta el `?domain=` de la URL. Devuelve `undefined` si falta o es invalido. */
export function parseDomainParam(raw: string | null): Domain | undefined {
  if (raw === null || raw === '') return undefined;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as Domain) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Ruta del cliente para un modelo. Un solo lugar decide la forma de la URL.
 *
 * El menu viaja como parametro de busqueda y no en la ruta porque no cambia QUE
 * se muestra sino DESDE DONDE se entro: `/m/base.partner?menu=base.menu_contacts_customers`
 * y `/m/base.partner` son la misma pantalla con distinto filtro base. Ademas es
 * lo que permite distinguir «Clientes» de «Proveedores», que comparten modelo.
 */
export function modelPath(modelName: string, menuName?: string | null): string {
  const base = `/m/${encodeURIComponent(modelName)}`;
  if (menuName === undefined || menuName === null || menuName === '') return base;
  return `${base}?${MENU_PARAM}=${encodeURIComponent(menuName)}`;
}

export function recordPath(modelName: string, id: number | 'new', menuName?: string | null): string {
  const base = `/m/${encodeURIComponent(modelName)}/${id}`;
  if (menuName === undefined || menuName === null || menuName === '') return base;
  return `${base}?${MENU_PARAM}=${encodeURIComponent(menuName)}`;
}

/** Ruta de un nodo de menu concreto. `null` si el nodo no lleva a ningun lado. */
export function menuPath(node: MenuNode): string | null {
  if (node.model === null) return null;
  return modelPath(node.model, node.name);
}

/** Un nodo es navegable si tiene modelo resuelto desde su accion. */
export function isLeaf(node: MenuNode): boolean {
  return node.model !== null && node.children.length === 0;
}

export interface FlatMenuEntry {
  node: MenuNode;
  /** Cadena de etiquetas desde la raiz hasta el nodo, incluido. */
  path: string[];
  depth: number;
  /**
   * Sinonimos del nodo y de todos sus ancestros, en una sola cadena.
   *
   * Van juntos porque una pantalla se busca tambien por el nombre de su area:
   * «En curso» es una pantalla de expensas aunque la palabra viva en la rama y
   * no en la hoja.
   */
  synonyms: string;
}

/** Aplana el arbol conservando la ruta: es lo que consume la paleta de comandos. */
export function flattenMenus(
  nodes: MenuNode[],
  ancestors: string[] = [],
  synonyms = '',
): FlatMenuEntry[] {
  const salida: FlatMenuEntry[] = [];
  for (const node of nodes) {
    const path = [...ancestors, node.label];
    const propios = `${synonyms} ${(node.keywords ?? []).join(' ')}`.trim();
    salida.push({ node, path, depth: ancestors.length, synonyms: propios });
    if (node.children.length > 0) {
      salida.push(...flattenMenus(node.children, path, propios));
    }
  }
  return salida;
}

/** Solo las entradas que llevan a algun lado. */
export function navigableMenus(nodes: MenuNode[]): FlatMenuEntry[] {
  return flattenMenus(nodes).filter((entry) => entry.node.model !== null);
}

/** Cadena de nodos desde la raiz hasta el que apunta a `modelName`. */
export function findMenuPath(nodes: MenuNode[], modelName: string): MenuNode[] {
  for (const node of nodes) {
    if (node.model === modelName) return [node];
    const resto = findMenuPath(node.children, modelName);
    if (resto.length > 0) return [node, ...resto];
  }
  return [];
}

/** Cadena de nodos desde la raiz hasta el menu de nombre `menuName`. */
export function findMenuPathByName(nodes: MenuNode[], menuName: string): MenuNode[] {
  for (const node of nodes) {
    if (node.name === menuName) return [node];
    const resto = findMenuPathByName(node.children, menuName);
    if (resto.length > 0) return [node, ...resto];
  }
  return [];
}

/** El nodo de nombre `menuName`, o `null`. Es de donde salen `domain` y `view_modes`. */
export function findMenuByName(nodes: MenuNode[], menuName: string): MenuNode | null {
  const ruta = findMenuPathByName(nodes, menuName);
  return ruta[ruta.length - 1] ?? null;
}

/**
 * Primer menu navegable que apunta a `modelName`.
 *
 * Es el que se resalta cuando se entro por una URL sin `?menu=`: sin esto, los
 * cuatro menus de Contactos se marcarian activos a la vez, que es justamente el
 * problema que el parametro viene a resolver.
 */
export function firstMenuForModel(nodes: MenuNode[], modelName: string): MenuNode | null {
  for (const node of nodes) {
    if (node.model === modelName) return node;
    const hijo = firstMenuForModel(node.children, modelName);
    if (hijo !== null) return hijo;
  }
  return null;
}

/**
 * `true` si el nodo es, o contiene, al menu de nombre `menuName`.
 *
 * Es por NOMBRE y no por modelo: preguntar "¿esta rama contiene a `base.partner`?"
 * da que si para las cuatro entradas de Contactos, que es exactamente lo que hay
 * que poder distinguir.
 */
export function containsMenu(node: MenuNode, menuName: string): boolean {
  if (node.name === menuName) return true;
  return node.children.some((hijo) => containsMenu(hijo, menuName));
}

/**
 * Todo el texto con el que se puede encontrar un nodo: su rotulo, su modelo y
 * los sinonimos que declaro el modulo.
 *
 * Los sinonimos son la mitad que faltaba. El rotulo de una pantalla es el nombre
 * tecnico del registro y casi nunca es la palabra del oficio: la administracion
 * escribia «expensas» —la palabra con la que nombra su trabajo del mes, y la que
 * el manual usa como ejemplo— y recibia «Ninguna pantalla coincide», porque la
 * pantalla se llama «Liquidacion» y ningun rotulo dice «expensas». Ver
 * `Menu.keywords` en el backend.
 */
function textoBuscable(node: MenuNode): string {
  return normalize(`${node.label} ${node.model ?? ''} ${(node.keywords ?? []).join(' ')}`);
}

/**
 * Poda el arbol dejando solo lo que coincide con `consulta`, conservando los
 * ancestros de cada coincidencia para no dejar hojas huerfanas.
 *
 * Es lo que hace usable una barra lateral de 206 nodos: con doce modulos
 * instalados, llegar a «Puntos de venta» son tres despliegues y cuatro pantallas
 * de scroll; escribir "punto" son cero. Una rama que coincide por su propio
 * nombre se muestra entera, porque buscar "Compras" tiene que traer Compras.
 *
 * Un nodo coincide por lo suyo **o por lo de sus ancestros**: el area es parte
 * del nombre de la pantalla aunque no este escrita en el rotulo. Por eso
 * `heredado` baja por la recursion —«En curso» cuelga de «Consorcios ›
 * Liquidacion», asi que se encuentra buscando "consorcios" y tambien
 * "expensas", que es un sinonimo de la rama—.
 */
export function filterMenus(nodes: MenuNode[], consulta: string, heredado = ''): MenuNode[] {
  const busqueda = normalize(consulta);
  if (busqueda === '') return nodes;

  const salida: MenuNode[] = [];
  for (const node of nodes) {
    const propio = textoBuscable(node);
    if (`${heredado} ${propio}`.includes(busqueda)) {
      salida.push(node);
      continue;
    }
    const hijos = filterMenus(node.children, consulta, `${heredado} ${propio}`);
    if (hijos.length > 0) salida.push({ ...node, children: hijos });
  }
  return salida;
}

/** Cuantos nodos tiene el arbol, contando ramas y hojas. */
export function countMenus(nodes: MenuNode[]): number {
  return nodes.reduce((total, node) => total + 1 + countMenus(node.children), 0);
}

/**
 * Normaliza para buscar sin acentos ni mayusculas: quien escribe "liquidacion"
 * tiene que encontrar "Liquidación".
 */
export function normalize(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Coincidencia por subsecuencia: "liqex" encuentra "Liquidación de expensas".
 * Devuelve un puntaje (mayor es mejor) o `null` si no hay coincidencia.
 *
 * Puntua mas alto el prefijo exacto y los caracteres consecutivos, que es lo que
 * hace que el primer resultado sea casi siempre el que uno queria.
 *
 * **Varias palabras se puntuan por separado y el orden no importa.** La
 * subsecuencia sobre la consulta entera exige que las letras aparezcan EN ORDEN
 * en el texto, y eso rompia justo lo que uno escribe cuando no se acuerda del
 * nombre exacto: la pantalla se llama «Todos los partes» y cuelga de
 * «Proyectos › Horas», asi que buscar "parte de horas" —el nombre del oficio—
 * devolvia CERO resultados en una paleta de 155 entradas. Partiendo por palabra
 * y exigiendo que todas coincidan, "parte de horas", "horas partes" y "partes"
 * llegan al mismo lado, y una consulta de una sola palabra recorre exactamente
 * el camino de antes.
 */
export function fuzzyScore(texto: string, consulta: string): number | null {
  const objetivo = normalize(texto);
  const busqueda = normalize(consulta);
  if (busqueda === '') return 0;

  const frase = puntuarTermino(objetivo, busqueda);

  const palabras = busqueda.split(/\s+/).filter((palabra) => palabra !== '');
  if (palabras.length < 2) return frase;

  let total = 0;
  let porPalabras: number | null = 0;
  for (const palabra of palabras) {
    const parcial = puntuarTermino(objetivo, palabra);
    // Todas las palabras tienen que estar: son un AND, no un OR. Si no, buscar
    // "tickets sla" traeria todos los tickets y ademas todos los SLA.
    if (parcial === null) {
      porPalabras = null;
      break;
    }
    total += parcial;
  }
  // Se promedia para que el puntaje no dependa de cuantas palabras se escribieron,
  // y se descuenta un poco: a igualdad de aciertos gana la coincidencia contigua,
  // que es la que el usuario escribio tal cual.
  if (porPalabras !== null) porPalabras = total / palabras.length - 25;

  if (frase === null && porPalabras === null) return null;
  return Math.max(frase ?? -Infinity, porPalabras ?? -Infinity);
}

/** Puntaje de un termino suelto contra un texto ya normalizado. */
function puntuarTermino(objetivo: string, termino: string): number | null {
  if (objetivo.startsWith(termino)) return 1000 - objetivo.length;

  const indiceDirecto = objetivo.indexOf(termino);
  if (indiceDirecto >= 0) return 700 - indiceDirecto - objetivo.length / 100;

  let puntaje = 0;
  let posicion = 0;
  let consecutivos = 0;
  for (const caracter of termino) {
    const encontrado = objetivo.indexOf(caracter, posicion);
    if (encontrado < 0) return null;
    consecutivos = encontrado === posicion ? consecutivos + 1 : 0;
    puntaje += 10 + consecutivos * 5 - Math.min(encontrado - posicion, 10);
    posicion = encontrado + 1;
  }
  return puntaje;
}
